// the main page

import React, { useState, useEffect, useContext, Suspense, useMemo } from "react";
import PropTypes from "prop-types";
import moment from "moment-timezone";
import { LargeGraphViewContext } from "../../__state/LargeGraphView";
import { useDevices } from "../../utils/hooks";
import __isEmpty from "lodash/isEmpty";

import "./WeatherDisplay.scss";

import {
  GoogleMap,
  MarkerF,
  InfoWindowF,
  useLoadScript,
} from "@react-google-maps/api";

import HeaderDefault from "../../components/HeaderDefault";
import DeviceFilterView from "./DeviceFilterView";
import Snackbar from "@mui/material/Snackbar";
import SnackbarContent from '@mui/material/SnackbarContent';

moment.tz.setDefault("UTC");

// Lazy-loaded components for better performance
const SingleDeviceView = React.lazy(() => import("./SingleDeviceView"));
const LargeGraphView = React.lazy(() => import("./LargeGraphView"));

const mapContainerStyle = {
  width: "100%",
  height: "100vh", //  Ensures the map is visible
};

const WeatherDisplay = ({ detectValidToken }) => {
  const selfData = JSON.parse(sessionStorage.getItem("selfData")) || {};
  const { metricValOn } = selfData;

  const initialCenter = { lat: 10.4806, lng: -66.9036 };
  const [center, _] = useState(initialCenter); // eslint-disable-line no-unused-vars

  const [selectedDevice, setSelectedDevice] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [markerHovered, setMarkerHovered] = useState(null);
  const [isResettingDevices, setIsResettingDevices] = useState(false);
  const [mapRef, setMapRef] = useState(null);
  const [tableIndex, setTableIndex] = useState(0);

  const { graphToShow } = useContext(LargeGraphViewContext);
  const { devices, setDevices, totalDeviceCount } = useDevices(
    isLoggingOut,
    setIsLoading,
    detectValidToken
  );

  //  Load Google Maps API
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY, 
  });

  useEffect(() => {
    if (detectValidToken) detectValidToken();
  }, [detectValidToken]);

  useEffect(() => {
    if (devices.length === 0 && !isLoading) {
      setIsLoading(true);
    }
  }, [devices, isLoading]);

  const parsedLat = useMemo(() => parseFloat(selectedDevice.lat?.[0]?.value), [selectedDevice]);
  const parsedLon = useMemo(() => parseFloat(selectedDevice.lon?.[0]?.value), [selectedDevice]);
  useEffect(() => {
    if (
      __isEmpty(selectedDevice) ||
      __isEmpty(selectedDevice.lat?.[0]) ||
      isNaN(selectedDevice.lat[0].value)
    ) {
      if (mapRef) {
        mapRef.setZoom(window.innerWidth > 1920 ? 4 : 2);
      }
      return;
    }

    if (!isNaN(parsedLat) && !isNaN(parsedLon) && mapRef) {
      mapRef.panTo({ lat: parsedLat, lng: parsedLon });
      mapRef.setZoom(16);
    }
  }, [mapRef, selectedDevice, parsedLat, parsedLon]);

  

  const markers = useMemo(() => {
    const handleMarkerHover = (device) => {
      if (!markerHovered || device.imei !== markerHovered.imei) {
        setMarkerHovered(device);
      }
    };

    return devices.map((device, i) => {
      if (!device.lat?.[0]?.value) return null;
      return (
        <MarkerF
          key={`${device.imei}-${i}`}
          position={{
            lat: parseFloat(device.lat[0].value),
            lng: parseFloat(device.lon[0].value),
          }}
          icon={`https://maps.google.com/mapfiles/ms/icons/${device.color}-dot.png`}
          onClick={() => setSelectedDevice(device)}
          onMouseOver={() => handleMarkerHover(device)}
        />
      );
    });
  }, [devices, markerHovered]);

  // const markers = useMemo(() => {
  //   return devices.map((device, i) => {
  //     if (!device.lat?.[0]?.value) return null;
  //     return (
  //       <MarkerF
  //         key={`${device.imei}-${i}`}
  //         position={{
  //           lat: parseFloat(device.lat[0].value),
  //           lng: parseFloat(device.lon[0].value),
  //         }}
  //         icon={`https://maps.google.com/mapfiles/ms/icons/${device.color}-dot.png`}
  //         onClick={() => setSelectedDevice(device)}
  //         onMouseOver={() => handleMarkerHover(device)}
  //       />
  //     );
  //   });
  // }, [devices]);

  if (!isLoaded) return <div>Loading Maps...</div>; //  Prevents crashing if API is not loaded

  return (
    <div className="weather-display-wrapper">
      <HeaderDefault setIsLoggingOut={setIsLoggingOut} />
      <Snackbar open={isLoading} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <SnackbarContent sx={{ backgroundColor: "white", color: "black" }} message="Loading Data..." />
      </Snackbar>
      <GoogleMap
        onLoad={(map) => setMapRef(map)}
        mapContainerStyle={mapContainerStyle}
        center={center}
        zoom={2}
        options={{ disableDefaultUI: true, minZoom: 2, maxZoom: 20 }}
      >
        {markers}
        {markerHovered && (
          <InfoWindowF position={{ 
            lat: parseFloat(markerHovered.lat[0].value), 
            lng: parseFloat(markerHovered.lon[0].value) 
          }}>
            <div>{markerHovered.imei}</div>
          </InfoWindowF>
        )}
      </GoogleMap>
      <DeviceFilterView
        devices={devices}
        setDevices={setDevices}
        totalDeviceCount={totalDeviceCount}
        selectedDevice={selectedDevice}
        setSelectedDevice={setSelectedDevice}
        setIsResettingDevices={setIsResettingDevices}
        tableIndex={tableIndex}
        setTableIndex={setTableIndex}
      />
      <Suspense fallback={<div className="fallback-loading">Loading View...</div>}>
        <LargeGraphView isOpen={graphToShow !== ""} showMetricOnly={metricValOn} />
        {!__isEmpty(selectedDevice) && (
          <SingleDeviceView
            device={selectedDevice}
            areEntriesLoading={isLoading}
            setAreEntriesLoading={setIsLoading}
            detectValidToken={detectValidToken}
            isResettingDevices={isResettingDevices}
            setIsResettingDevices={setIsResettingDevices}
            tableIndex={tableIndex}
            setTableIndex={setTableIndex}
          />
        )}
      </Suspense>
    </div>
  );
};

WeatherDisplay.propTypes = {
  detectValidToken: PropTypes.func,
};

export default WeatherDisplay;
